import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { FiEye } from "react-icons/fi";
import { HiPlay, HiSparkles } from "react-icons/hi2";
import ClientCalling from "../../api/ClientCalling";
import StoryViewer from "./StoryViewer";
import { formatView } from "../formatView";
import SmartImage from "./SmartImage";

const AUTO_SCROLL_SPEED = 25; // pixels per second

const CardSkeleton = () => (
    <div className='relative w-32 h-52 sm:w-36 sm:h-60 md:w-52 md:h-80 rounded-2xl ios-shimmer flex-shrink-0'>
        {/* View badge placeholder */}
        <div
            className='absolute top-2 left-2 w-12 h-5 rounded-full'
            style={{
                background: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(10px)",
            }}
        />
        {/* Play button placeholder */}
        <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
            <div
                className='w-10 h-10 md:w-12 md:h-12 rounded-full'
                style={{
                    background: "rgba(255,255,255,0.08)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,255,255,0.12)",
                }}
            />
        </div>
        {/* Title placeholder */}
        <div className='absolute inset-x-0 bottom-2 px-2 md:px-3'>
            <div
                className='h-3 rounded-md w-3/4'
                style={{ background: "rgba(255,255,255,0.12)" }}
            />
        </div>
    </div>
);

const ClientReels = () => {
    const [loader, setLoader] = useState(false);
    const [reelsData, setReelsData] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(6);
    const [totalPages, setTotalPages] = useState(1);
    const navigate = useNavigate();
    const observerRef = useRef(null);
    const scrollRef = useRef(null);
    const rafRef = useRef(null);
    const lastTimeRef = useRef(0);
    const autoScrollPausedRef = useRef(false);

    const [showStory, setShowStory] = useState(false);
    const [storyStartIndex, setStoryStartIndex] = useState(0);

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const fetchData = async (append = false) => {
        try {
            setLoader(true);
            const res = await ClientCalling?.getReelsClient(page, limit);
            if (res?.status) {
                setReelsData((prev) =>
                    append ? [...prev, ...res.data] : res.data
                );
            } else {
                if (!append) setReelsData([]);
            }
            setTotalPages(res?.totalPages || 1);
        } catch (err) {
            console.error("Reels fetch failed:", err);
            navigate("/error");
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData(page > 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const lastReelRef = useCallback(
        (node) => {
            if (loader) return;
            if (observerRef.current) observerRef.current.disconnect();
            observerRef.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && page < totalPages) {
                    setPage((p) => p + 1);
                }
            });
            if (node) observerRef.current.observe(node);
        },
        [loader, page, totalPages]
    );

    const updateScrollArrows = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setCanPrev(el.scrollLeft > 8);
        setCanNext(el.scrollLeft < maxScroll - 8);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollArrows();
        el.addEventListener("scroll", updateScrollArrows, { passive: true });
        window.addEventListener("resize", updateScrollArrows);
        return () => {
            el.removeEventListener("scroll", updateScrollArrows);
            window.removeEventListener("resize", updateScrollArrows);
        };
    }, [reelsData, updateScrollArrows]);

    // Continuous rAF-based auto-scroll for buttery smoothness
    useEffect(() => {
        if (reelsData.length === 0) return;

        const tick = (time) => {
            if (lastTimeRef.current === 0) lastTimeRef.current = time;
            const dt = (time - lastTimeRef.current) / 1000;
            lastTimeRef.current = time;

            const el = scrollRef.current;
            if (
                el &&
                !autoScrollPausedRef.current &&
                !isDragging &&
                el.scrollWidth > el.clientWidth
            ) {
                const max = el.scrollWidth - el.clientWidth;
                if (el.scrollLeft >= max - 0.5) {
                    el.scrollLeft = 0;
                } else {
                    el.scrollLeft += AUTO_SCROLL_SPEED * dt;
                }
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(rafRef.current);
            lastTimeRef.current = 0;
        };
    }, [reelsData, isDragging]);

    const scrollByPx = (px) => {
        scrollRef.current?.scrollBy({ left: px, behavior: "smooth" });
    };

    // Manual prev/next buttons (one card at a time-ish)
    const scrollLeftBtn = () => scrollByPx(-340);
    const scrollRightBtn = () => scrollByPx(340);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        autoScrollPausedRef.current = true;
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
        scrollRef.current.style.cursor = "grabbing";
    };
    const handleMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
            if (scrollRef.current) scrollRef.current.style.cursor = "grab";
        }
    };
    const handleMouseUp = () => {
        setIsDragging(false);
        if (scrollRef.current) scrollRef.current.style.cursor = "grab";
    };
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 1.6;
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };
    const handleTouchStart = (e) => {
        autoScrollPausedRef.current = true;
        setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };
    const handleTouchMove = (e) => {
        const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 1.6;
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };
    const handleTouchEnd = () => {
        setTimeout(() => {
            autoScrollPausedRef.current = false;
        }, 1500);
    };

    const handleStoryClick = (index) => {
        setStoryStartIndex(index);
        setShowStory(true);
    };

    const handlePauseHover = () => {
        autoScrollPausedRef.current = true;
    };
    const handleResumeHover = () => {
        if (!isDragging) {
            setTimeout(() => {
                autoScrollPausedRef.current = false;
            }, 200);
        }
    };

    return (
        <div className='w-full px-3 sm:px-4 my-6 md:my-10'>
            <div className='mx-auto w-full max-w-7xl'>
                {/* Header */}
                <div className='flex items-end justify-between gap-3 mb-4 md:mb-6'>
                    <div className='flex items-center gap-3'>
                        <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            whileInView={{ scale: 1, rotate: 0 }}
                            viewport={{ once: true }}
                            transition={{ type: "spring", stiffness: 200, damping: 18 }}
                            className='ios-glass w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center'
                            style={{ color: "#FCFA02" }}
                        >
                            <HiSparkles className='text-lg md:text-2xl' />
                        </motion.div>
                        <div>
                            {loader && reelsData.length === 0 ? (
                                <>
                                    <div className='h-5 md:h-7 w-32 md:w-44 rounded-md ios-shimmer mb-1' />
                                    <div className='h-3 w-40 md:w-52 rounded-md ios-shimmer opacity-60' />
                                </>
                            ) : (
                                <>
                                    <motion.h2
                                        initial={{ x: -20, opacity: 0 }}
                                        whileInView={{ x: 0, opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5 }}
                                        className='text-lg md:text-2xl font-bold tracking-tight text-client-font flex items-center gap-2'
                                    >
                                        Trending{" "}
                                        <span
                                            style={{
                                                background:
                                                    "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                                WebkitBackgroundClip: "text",
                                                WebkitTextFillColor: "transparent",
                                                backgroundClip: "text",
                                            }}
                                        >
                                            Stories
                                        </span>
                                    </motion.h2>
                                    <motion.p
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.2, duration: 0.5 }}
                                        className='text-[11px] md:text-xs text-client-font/55 mt-0.5'
                                    >
                                        Tap any story to start watching
                                    </motion.p>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Scroll arrows */}
                    <div className='flex items-center gap-2'>
                        <motion.button
                            onClick={scrollLeftBtn}
                            disabled={!canPrev}
                            whileHover={canPrev ? { scale: 1.08 } : {}}
                            whileTap={canPrev ? { scale: 0.9 } : {}}
                            animate={{ opacity: canPrev ? 1 : 0.35 }}
                            aria-label='Scroll left'
                            className='ios-glass-btn w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white disabled:cursor-not-allowed'
                        >
                            <MdKeyboardArrowLeft className='text-xl md:text-2xl' />
                        </motion.button>
                        <motion.button
                            onClick={scrollRightBtn}
                            disabled={!canNext}
                            whileHover={canNext ? { scale: 1.08 } : {}}
                            whileTap={canNext ? { scale: 0.9 } : {}}
                            animate={{ opacity: canNext ? 1 : 0.35 }}
                            aria-label='Scroll right'
                            className='ios-glass-btn w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white disabled:cursor-not-allowed'
                        >
                            <MdKeyboardArrowRight className='text-xl md:text-2xl' />
                        </motion.button>
                    </div>
                </div>

                {/* Reels list */}
                <div
                    className='relative'
                    onMouseEnter={handlePauseHover}
                    onMouseLeave={handleResumeHover}
                >
                    {/* Edge fade gradients */}
                    <div
                        className='absolute left-0 top-0 bottom-0 w-8 md:w-12 pointer-events-none z-10'
                        style={{
                            background:
                                "linear-gradient(90deg, rgba(10,10,10,0.6), transparent)",
                            opacity: canPrev ? 1 : 0,
                            transition: "opacity 0.3s ease",
                        }}
                    />
                    <div
                        className='absolute right-0 top-0 bottom-0 w-8 md:w-12 pointer-events-none z-10'
                        style={{
                            background:
                                "linear-gradient(-90deg, rgba(10,10,10,0.6), transparent)",
                            opacity: canNext ? 1 : 0,
                            transition: "opacity 0.3s ease",
                        }}
                    />

                    <div
                        ref={scrollRef}
                        className='flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide py-2 select-none'
                        style={{
                            cursor: "grab",
                            scrollbarWidth: "none",
                            scrollBehavior: "auto",
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleMouseLeave}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {loader && page === 1 && reelsData.length === 0 ? (
                            [...Array(limit)].map((_, idx) => <CardSkeleton key={idx} />)
                        ) : reelsData.length === 0 ? (
                            <div className='w-full text-center py-10 text-client-font/50 text-sm'>
                                No stories yet
                            </div>
                        ) : (
                            <>
                                {reelsData.map((reel, index) => {
                                    const isLast = index === reelsData.length - 1;
                                    return (
                                        <motion.div
                                            key={reel._id}
                                            ref={isLast ? lastReelRef : null}
                                            onClick={(e) => {
                                                if (isDragging) {
                                                    e.preventDefault();
                                                    return;
                                                }
                                                handleStoryClick(index);
                                            }}
                                            initial={{ opacity: 0, y: 24 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true, amount: 0.2 }}
                                            transition={{
                                                duration: 0.45,
                                                delay: Math.min(index * 0.04, 0.4),
                                                ease: [0.22, 1, 0.36, 1],
                                            }}
                                            whileHover={{ y: -6, scale: 1.025 }}
                                            whileTap={{ scale: 0.97 }}
                                            className='relative w-32 h-52 sm:w-36 sm:h-60 md:w-52 md:h-80 rounded-2xl cursor-pointer flex-shrink-0 group bg-client-back'
                                            style={{
                                                overflow: "clip",
                                                boxShadow:
                                                    "0 10px 30px -10px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)",
                                            }}
                                        >
                                            <SmartImage
                                                src={reel.l_id.l_image}
                                                alt={reel.l_id.l_name}
                                                className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
                                            />

                                            {/* Bottom gradient overlay */}
                                            <div
                                                className='absolute inset-x-0 bottom-0 h-2/5 pointer-events-none'
                                                style={{
                                                    background:
                                                        "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
                                                }}
                                            />

                                            {/* View count */}
                                            <div className='absolute top-2 left-2 ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[9px] md:text-[11px] font-medium pointer-events-none'>
                                                <FiEye className='text-[10px] md:text-xs' />
                                                <span className='tabular-nums'>
                                                    {formatView(reel.l_view)}
                                                </span>
                                            </div>

                                            {/* Play button */}
                                            <motion.div
                                                className='absolute inset-0 flex items-center justify-center pointer-events-none'
                                                initial={{ opacity: 0.85 }}
                                                whileHover={{ opacity: 1 }}
                                            >
                                                <motion.div
                                                    className='ios-glass w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white'
                                                    animate={{ scale: [1, 1.08, 1] }}
                                                    transition={{
                                                        duration: 2.4,
                                                        repeat: Infinity,
                                                        ease: "easeInOut",
                                                    }}
                                                    style={{
                                                        boxShadow:
                                                            "0 6px 18px rgba(0,0,0,0.4), inset 0 1px 0 0 rgba(255,255,255,0.25)",
                                                    }}
                                                >
                                                    <HiPlay className='text-base md:text-lg translate-x-[1px]' />
                                                </motion.div>
                                            </motion.div>

                                            {/* Bottom name */}
                                            <div className='absolute bottom-0 left-0 right-0 p-2 md:p-3 pointer-events-none'>
                                                <p
                                                    className='text-white text-[10px] md:text-xs font-semibold truncate'
                                                    style={{
                                                        textShadow:
                                                            "0 1px 3px rgba(0,0,0,0.7)",
                                                    }}
                                                >
                                                    {reel?.l_id?.l_name}
                                                </p>
                                            </div>

                                            {/* Hover glow ring */}
                                            <div
                                                className='absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
                                                style={{
                                                    boxShadow:
                                                        "inset 0 0 0 2px rgba(127,0,255,0.5), 0 0 30px rgba(127,0,255,0.4)",
                                                }}
                                            />
                                        </motion.div>
                                    );
                                })}

                                {/* Loading more shimmer */}
                                {loader &&
                                    page > 1 &&
                                    [...Array(3)].map((_, idx) => (
                                        <CardSkeleton key={`load-${idx}`} />
                                    ))}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {showStory && (
                <StoryViewer
                    stories={{ ...reelsData, data: reelsData }}
                    startIndex={storyStartIndex}
                    onClose={() => setShowStory(false)}
                />
            )}
        </div>
    );
};

export default ClientReels;
